import React from 'react'
import PropTypes from 'prop-types'

import { get, noop, some, isString, isUndefined } from 'lodash'
import { Button, Box, Link } from '@material-ui/core'

import { NonDismissibleDialog } from '@scoir/modals'
import PdfDocumentViewer from '@scoir/components-core/dist/components/PdfDocumentViewer'
import Icon from '@scoir/components-core/dist/components/Icon'
import MyCollegesSecondary from '@scoir/brand-icons/dist/MyCollegesSecondary'
import { Pill } from '@scoir/pills'

import api, { pdf } from 'common/api'
import schoolIcon from 'icons/local-library.svg'
import helpers from '../Helpers'
import PercentRing from './PercentRing'
import HistoricalApplicationsChart from './HistoricalApplicationsChart'
import './index.scss'

const { Body, Footer } = NonDismissibleDialog
const { CloseButton } = Footer

const getGradeOffered = (string) =>
  isString(string) ? string.replace(/[^0-9]+/g, '').replace(/^0*/, '') || 'N/A' : 'N/A'

const prependHttpIfNecessary = (url) => (url.match(new RegExp('^(http://|https://)')) === null ? `http://${url}` : url)

const commas = (num) => num.toFixed().replace(new RegExp(/(\d)(?=(\d{3})+$)/g), '$1,')

class Overview extends React.Component {
  static propTypes = {
    highSchool: PropTypes.object,
  }
  state = {
    applicationHistory: [],
    isModalOpen: false,
    blobUrl: undefined,
  }
  componentDidMount() {
    this.loadApplicationHistory()
  }
  componentDidUpdate(prevProps) {
    const id = get(this.props.highSchool, 'Id')
    const oldId = get(prevProps.highSchool, 'Id')
    if (id !== oldId) {
      this.loadApplicationHistory()
    }
  }
  loadApplicationHistory() {
    const id = get(this.props.highSchool, 'Id')
    if (id) {
      api
        .get(`/api/highschools/visits/applicants/${id}`)
        .then((applicationHistory) => {
          this.setState({
            applicationHistory,
          })
        })
        .catch(noop)
    }
  }
  showSchoolProfile(highSchoolId) {
    pdf
      .get(`/api/highschools/visits/profiles/${highSchoolId}`)
      .then((pdfData) => {
        if (this.state.blobUrl) {
          window.URL.revokeObjectURL(this.state.blobUrl)
        }
        const blob = new Blob([pdfData], { type: 'application/pdf', length: pdfData.length })
        const blobUrl = window.URL.createObjectURL(blob)

        this.setState({
          isModalOpen: true,
          pdfData,
          blobUrl,
        })
      })
      .catch(noop)
  }
  hidePdf() {
    this.setState({
      isModalOpen: false,
      pdfData: undefined,
    })
  }

  render() {
    const hs = this.props.highSchool
    const hasSeniors = hs.TwelfthGradeStudents > 0
    const highGradeOffered = getGradeOffered(hs.HighGrade)
    const lowGradeOffered = getGradeOffered(hs.LowGrade)
    const eitherGradeOffered = highGradeOffered !== 'N/A' || lowGradeOffered !== 'N/A'
    const hasReligiousAffiliation = hs.ReligiousAffiliation !== '' && !isUndefined(hs.ReligiousAffiliation)
    const hasSchoolType = hs.SchoolType !== '' && !isUndefined(hs.SchoolType)
    const hasCollegeBound = hs.SchoolType === 'Private' && hs.PercentToFourYear > 0
    const hasReducedLunch = hs.TotalStudentsApprovedForFreeLunch > 0 && hs.TotalStudents > 0
    const applicationHistory = this.state.applicationHistory
    const hasApplicationHistory = some(applicationHistory, (item) => item.NumApplicants > 0)
    const hasTitle1 = get(hs, 'IsTitle1Eligible.Value', false) === true
    const isCharterSchool = hs.IsCharterSchool && hs.IsCharterSchool.Value

    return (
      <div className="selection-overview">
        <div className="scrollable">
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <div className="section">
              <div className="title">Location</div>
              {hs.Address}
              {(hs.City || hs.State) && (
                <div className="city-state">
                  {helpers.commaSeparatedList([hs.City, hs.State])}
                  &nbsp;{hs.Zipcode}
                </div>
              )}
              <div>{hs.PhoneNumber}</div>
              {!isUndefined(hs.Website) && (
                <div>
                  <Link
                    className="website"
                    href={prependHttpIfNecessary(hs.Website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="primary"
                  >
                    {hs.Website}
                  </Link>
                </div>
              )}
              {!isUndefined(hs.ProfileUploadedBy) && (
                <div>
                  <Button
                    color="primary"
                    className="school-profile-link"
                    onClick={() => this.showSchoolProfile(hs.Id)}
                  >
                    View School Profile
                  </Button>
                </div>
              )}
            </div>
            <ul>
              <li>
                <Pill
                  color="successDark"
                  mb={1}
                >
                  {hasSchoolType && hs.SchoolType}
                </Pill>
              </li>
              {isCharterSchool && (
                <li>
                  <Pill color="info">Charter</Pill>
                </li>
              )}
            </ul>
          </Box>
          {hasReligiousAffiliation ? (
            <div className="section religious-affiliation">
              <div className="title">Religious Affiliation</div>
              {hs.ReligiousAffiliation}
            </div>
          ) : (
            hasSchoolType && (
              <div className="section educational-focus">
                <div className="title">Educational Focus</div>
                {hs.SchoolType}
              </div>
            )
          )}
          {(hasSeniors || eitherGradeOffered) && (
            <div className="section columns">
              {hasSeniors && (
                <div>
                  <div className="title">Senior Class</div>
                  <MyCollegesSecondary className="senior-icon" />
                  <span className="senior-count count">{commas(hs.TwelfthGradeStudents)}</span>
                </div>
              )}
              {eitherGradeOffered && (
                <div>
                  <div className="title">Grades Offered</div>
                  <Icon
                    def={schoolIcon}
                    size="lg"
                  />
                  <span className="class-range count">
                    {lowGradeOffered} &ndash; {highGradeOffered}
                  </span>
                </div>
              )}
            </div>
          )}
          {hasCollegeBound && (
            <div className="section columns">
              <div>
                <div className="title">College Bound</div>
                <small>
                  Students attending <br />
                  4-year colleges
                </small>
              </div>
              <div className="percentage text-center">
                <PercentRing
                  className="college-bound-percent"
                  percent={hs.PercentToFourYear}
                />
              </div>
            </div>
          )}
          {hasReducedLunch && (
            <div className="section columns">
              <div>
                <div className="title">Reduced Lunch</div>
                <small>Students receiving free or reduced lunch</small>
              </div>
              <div className="percentage text-center">
                <PercentRing
                  className="reduced-lunch-percent"
                  percent={Math.round((hs.TotalStudentsApprovedForFreeLunch * 100) / hs.TotalStudents)}
                />
              </div>
            </div>
          )}
          {hasTitle1 && (
            <div className="section">
              <Pill color="info">Title 1 Eligible School</Pill>
            </div>
          )}
          {hasApplicationHistory && (
            <div className="section application-history">
              <div className="title">Historical Applicants</div>
              <HistoricalApplicationsChart years={applicationHistory} />
            </div>
          )}
        </div>
        <NonDismissibleDialog
          headerText={hs.SchoolName}
          open={this.state.isModalOpen}
          onClose={() => this.hidePdf()}
        >
          <Body>
            <PdfDocumentViewer
              bytes={this.state.pdfData}
              pdfTitle={hs.SchoolName}
            />
            <Box mt={2}>
              <Link
                href={this.state.blobUrl}
                color="primary"
                rel="noopener noreferrer"
                target="_blank"
              >
                Open
              </Link>
            </Box>
          </Body>
          <Footer>
            <CloseButton>Cancel</CloseButton>
          </Footer>
        </NonDismissibleDialog>
      </div>
    )
  }
}

export default Overview
